import { useCallback } from 'react'
import { useWorld } from './use-world'
import { GRID_SIZE } from '../config/constants'

class PriorityQueue {
  constructor() {
    this.items = []
  }

  enqueue(item, priority) {
    this.items.push({ item, priority })
    this.items.sort((a, b) => a.priority - b.priority)
  }

  dequeue() {
    return this.items.shift()?.item
  }

  isEmpty() {
    return this.items.length === 0
  }
}

const heuristic = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y)
const posKey = (x, y) => `${x},${y}`

const getNeighbors = (pos, gridSize) => {
  const directions = [
    { x: gridSize, y: 0 },
    { x: -gridSize, y: 0 },
    { x: 0, y: gridSize },
    { x: 0, y: -gridSize },
    { x: gridSize, y: gridSize },
    { x: -gridSize, y: -gridSize },
    { x: gridSize, y: -gridSize },
    { x: -gridSize, y: gridSize }
  ]
  return directions.map((d) => ({ x: pos.x + d.x, y: pos.y + d.y }))
}

export const usePathfinding = () => {
  const { spritesRef, container } = useWorld()

  const isWalkable = useCallback(
    (x, y, width, height) => {
      if (x < 0 || y < 0) return false
      if (container && (x + width > container.width || y + height > container.height)) {
        return false
      }

      const testBox = { x, y, width, height }
      const sprites = spritesRef.current

      for (const key of Object.keys(sprites)) {
        if (key === 'player') continue
        // Only tile sprites block pathfinding; entities (NPCs, monsters) are
        // handled by real-time collision and must not block A* route planning.
        if (!key.startsWith('tile_')) continue
        const sprite = sprites[key]
        if (!sprite?.position) continue

        const spritePos = sprite.position
        if (
          spritePos.config?.solid !== false &&
          testBox.x < spritePos.x + spritePos.width &&
          testBox.x + testBox.width > spritePos.x &&
          testBox.y < spritePos.y + spritePos.height &&
          testBox.y + testBox.height > spritePos.y
        ) {
          return false
        }
      }

      return true
    },
    [spritesRef, container]
  )

  const findPath = useCallback(
    (start, goal, playerWidth, playerHeight) => {
      // Use the actual tile size from registered sprites so snapping and neighbor
      // generation align with real tile positions regardless of dungeon grid size.
      const sprites = spritesRef.current
      const tileKey = Object.keys(sprites).find(k => k.startsWith('tile_'))
      const gridSize = tileKey ? (sprites[tileKey].position.width || GRID_SIZE) : GRID_SIZE
      const checkWidth = gridSize
      const checkHeight = gridSize

      const startGrid = {
        x: Math.floor(start.x / gridSize) * gridSize,
        y: Math.floor(start.y / gridSize) * gridSize
      }
      const goalGrid = {
        x: Math.floor(goal.x / gridSize) * gridSize,
        y: Math.floor(goal.y / gridSize) * gridSize
      }

      if (startGrid.x === goalGrid.x && startGrid.y === goalGrid.y) {
        return [goalGrid]
      }

      // If the start cell is isolated (all 8 neighbours are solid walls), BFS
      // outward through the grid until we reach the nearest walkable cell.
      // The returned path will begin from that escape cell; the caller is
      // responsible for snapping the player there before following the path.
      const DIRS = [
        { x: gridSize, y: 0 }, { x: -gridSize, y: 0 },
        { x: 0, y: gridSize }, { x: 0, y: -gridSize },
        { x: gridSize, y: gridSize }, { x: -gridSize, y: -gridSize },
        { x: gridSize, y: -gridSize }, { x: -gridSize, y: gridSize },
      ]
      const startHasEscape = DIRS.some(d => isWalkable(startGrid.x + d.x, startGrid.y + d.y, checkWidth, checkHeight))
      if (!startHasEscape) {
        const escVis = new Set([posKey(startGrid.x, startGrid.y)])
        const escQ = [startGrid]
        let escape = null
        while (escQ.length > 0 && !escape) {
          const cur = escQ.shift()
          for (const d of DIRS) {
            const nx = cur.x + d.x, ny = cur.y + d.y
            const key = posKey(nx, ny)
            if (escVis.has(key)) continue
            escVis.add(key)
            if (isWalkable(nx, ny, checkWidth, checkHeight)) { escape = { x: nx, y: ny }; break }
            escQ.push({ x: nx, y: ny })
          }
        }
        if (!escape) return []
        startGrid.x = escape.x
        startGrid.y = escape.y
      }

      if (!isWalkable(goalGrid.x, goalGrid.y, checkWidth, checkHeight)) {
        // BFS outward from the clicked tile to find the nearest walkable tile
        const searched = new Set([posKey(goalGrid.x, goalGrid.y)])
        const queue = [{ x: goalGrid.x, y: goalGrid.y, dist: 0 }]
        let found = false
        const MAX_DIST = 20
        while (queue.length > 0 && !found) {
          const { x, y, dist } = queue.shift()
          if (dist >= MAX_DIST) continue
          for (const d of DIRS) {
            const nx = x + d.x
            const ny = y + d.y
            const key = posKey(nx, ny)
            if (searched.has(key)) continue
            searched.add(key)
            if (isWalkable(nx, ny, checkWidth, checkHeight)) {
              goalGrid.x = nx
              goalGrid.y = ny
              found = true
              break
            }
            queue.push({ x: nx, y: ny, dist: dist + 1 })
          }
        }
        if (!found) return []
      }

      const openSet = new PriorityQueue()
      const cameFrom = new Map()
      const gScore = new Map()
      const fScore = new Map()

      const startKey = posKey(startGrid.x, startGrid.y)
      gScore.set(startKey, 0)
      fScore.set(startKey, heuristic(startGrid, goalGrid))
      openSet.enqueue(startGrid, fScore.get(startKey))

      const visited = new Set()
      let iterations = 0
      const maxIterations = 1000

      while (!openSet.isEmpty() && iterations < maxIterations) {
        iterations++
        const current = openSet.dequeue()
        const currentKey = posKey(current.x, current.y)

        if (current.x === goalGrid.x && current.y === goalGrid.y) {
          const path = []
          let curr = current
          while (curr) {
            path.unshift({ x: curr.x, y: curr.y })
            curr = cameFrom.get(posKey(curr.x, curr.y))
          }
          return path
        }

        if (visited.has(currentKey)) continue
        visited.add(currentKey)

        for (const neighbor of getNeighbors(current, gridSize)) {
          const neighborKey = posKey(neighbor.x, neighbor.y)
          if (visited.has(neighborKey)) continue

          const isStart = neighbor.x === startGrid.x && neighbor.y === startGrid.y
          if (!isStart && !isWalkable(neighbor.x, neighbor.y, checkWidth, checkHeight)) continue

          const isDiagonal = neighbor.x !== current.x && neighbor.y !== current.y
          const moveCost = isDiagonal ? 1.414 : 1
          const tentativeG = (gScore.get(currentKey) ?? Infinity) + moveCost

          if (tentativeG < (gScore.get(neighborKey) ?? Infinity)) {
            cameFrom.set(neighborKey, current)
            gScore.set(neighborKey, tentativeG)
            const f = tentativeG + heuristic(neighbor, goalGrid)
            fScore.set(neighborKey, f)
            openSet.enqueue(neighbor, f)
          }
        }
      }

      return []
    },
    [isWalkable]
  )

  return { findPath, isWalkable }
}
